import type { ReactNode } from "react";
import { RefreshCw, Search } from "lucide-react";
import { Alert, Button, Card, IconButton, Tooltip, cn } from "@platform/ui";
import { isApiError } from "../api/errors";
import { useT } from "../i18n";

/** Paperbase content card: centered, max `--container-content` (1216px). */
export function PageCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Card className={cn("mx-auto w-full max-w-[var(--container-content)] overflow-hidden", className)}>
      {children}
    </Card>
  );
}

export function Toolbar({
  search,
  actions,
  onReload,
}: {
  search?: { value: string; onChange: (v: string) => void; placeholder: string; onSubmit?: () => void };
  actions?: ReactNode;
  onReload?: () => void;
}) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
      {search ? (
        <form
          role="search"
          className="relative flex max-w-xl min-w-48 flex-1 items-center"
          onSubmit={(e) => {
            e.preventDefault();
            search.onSubmit?.();
          }}
        >
          <Search className="pointer-events-none absolute left-3 size-4 text-ink-3" aria-hidden />
          <input
            type="search"
            aria-label={search.placeholder}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder}
            className="h-9 w-full rounded-[var(--radius-control)] border border-line-strong bg-paper pr-3 pl-9 text-sm shadow-[var(--shadow-xs)] transition-[border-color,box-shadow] outline-none placeholder:text-ink-3 hover:border-ink-3 focus:border-primary focus:ring-4 focus:ring-primary/15"
          />
        </form>
      ) : (
        <div className="flex-1" />
      )}
      {search && <div className="flex-1" />}
      {actions}
      {onReload && (
        <Tooltip content={t("common.reload")}>
          <IconButton label={t("common.reload")} onClick={onReload}>
            <RefreshCw className="size-4.5" aria-hidden />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}

/** Toolbar reload action (used next to DataGrid toolbars). */
export function ReloadButton({ onClick }: { onClick: () => void }) {
  const { t } = useT();
  return (
    <Tooltip content={t("common.reload")}>
      <IconButton label={t("common.reload")} onClick={onClick}>
        <RefreshCw className="size-4" aria-hidden />
      </IconButton>
    </Tooltip>
  );
}

export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useT();
  const msg = isApiError(error)
    ? (error.detail ?? error.title)
    : error instanceof Error
      ? error.message
      : String(error);
  return (
    <div className="p-4">
      <Alert
        tone="error"
        title={t("common.errorTitle")}
        action={
          onRetry && (
            <Button size="sm" variant="secondary" onClick={onRetry}>
              {t("common.retry")}
            </Button>
          )
        }
      >
        {msg}
      </Alert>
    </div>
  );
}

export function Section({
  title,
  children,
  actions,
}: {
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className="border-b border-line px-6 py-5 last:border-b-0">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="flex-1 text-base font-semibold">{title}</h2>
        {actions}
      </div>
      {children}
    </section>
  );
}

export function TableShell({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table aria-label={label} className="w-full min-w-[640px] border-collapse text-left text-sm">
        {children}
      </table>
    </div>
  );
}

export const th =
  "border-b border-line bg-subtle px-4 py-2.5 text-xs font-medium text-ink-2 whitespace-nowrap";
export const td = "border-b border-line px-4 py-3 align-middle";
